/**
 * @packageDocumentation
 * Controls automated spin sessions, handling countdowns, UI state, and callbacks that drive repeated rounds without manual input.
 */
// Autoplay.ts
import * as PIXI from "pixi.js";
import { CardScreen, onSpinClicked } from "./CardScreen";
import { betArea } from "./betArea";
import { betSizes } from "./server";

import { LogoAnim } from "../app";

import { brightnessFilter, disabledfilter, HandleOrientation } from "./global";
import { SoundManager } from "./sounds";
import { EventBus, EnumEventBus } from "../EventBus";
import addInteraction from "../lib/addButtonInteraction";

type SpinFn = () => Promise<void>;

type CountChoice = 5 | 10 | 15 | 20;

export class AutoplayManager extends PIXI.Container {
  private screen: CardScreen | null = null;
  private spin: SpinFn;
  private screenGetter?: () => CardScreen | undefined;
  private isScreenWired = false;

  private modal!: PIXI.Container;
  private closeBtn!: PIXI.Sprite;

  // buttons inside modal
  private countButtons = new Map<CountChoice, PIXI.Container>();
  private highlight!: PIXI.Sprite;  // "but_autocount_shape" moved behind selected
  private countLabel!: PIXI.Text;

  // state
  private selected: CountChoice = 5;
  private isRunning = false;
  private inRound = false;
  private left = 0;   // rounds left; 0 means stopped

  constructor(screenOrGetter: CardScreen | (() => CardScreen | undefined), spinFn?: SpinFn) {
    super();

    if (typeof screenOrGetter === "function") {
      this.screenGetter = screenOrGetter;
      this.screen = this.screenGetter() ?? null;
    } else {
      this.screen = screenOrGetter ?? null;
    }

    this.spin = spinFn ?? (() => onSpinClicked(this.ensureScreen()));

    this.buildUI();
    this.tryWireScreen();
    // setTimeout(() => {
      HandleOrientation(this.PortraitAdjustFun.bind(this),this.landScapeAdjustFun.bind(this))
    // }, 1000);
    EventBus.on(EnumEventBus.setAutoplayPopup, (v: boolean) => v ? this.openModal() : this.closeModal());
    EventBus.on(EnumEventBus.setAutoplayGame, (v: boolean, count: number) => v ? this.start(count as CountChoice) : this.stop());
  }

  // ---------------- UI ----------------
  private buildUI() {

    // 3) Modal
    this.modal = this.buildModal();
    this.modal.visible = false;
    this.addChild(this.modal);

    // layout once and on resize
    //this.layout();
    //window.addEventListener?.("resize", () => this.layout());
  }

  private layout() {
    // size overlay to the stage
    const w = (this.parent as any)?.width ?? (globalThis as any).innerWidth ?? 1920;
    const h = (this.parent as any)?.height ?? (globalThis as any).innerHeight ?? 1080;

    // center modal
    this.modal.x = 0;
    this.modal.y = 0;

    if (window.matchMedia("(orientation:portrait)").matches) {
        this.PortraitAdjustFun();
    } else { 
        this.landScapeAdjustFun();
    }   
  }

  private buildModal(): PIXI.Container {
    const c = new PIXI.Container();

    // (1) background sprite that fills the modal
    const bg = new PIXI.Sprite(PIXI.Assets.get("autoplay_bg"));
    bg.interactive = true;
    // Optionally scale to your desired size; here we use bg's natural size.
    c.addChild(bg);

    // (2) title "AUTOPLAY"
    const title = new PIXI.Text("AUTOPLAY", new PIXI.TextStyle({
      fill: 0xffffff,
      fontSize: 70,
      fontWeight: "900",
      dropShadow: {
        alpha: 1,
        angle: 1,
        blur: 2,
        color: "0x111111",
        distance: 2,
      },
      stroke: {
        color: "black",
        width: 5,
        join: "round",
      },
    }));
    title.anchor.set(0.5, 0);
    title.x = bg.width / 2;
    title.y = 125;
    c.addChild(title);

    // (9) close button on right side
    this.closeBtn = new PIXI.Sprite(PIXI.Assets.get("close_btn"));
    this.closeBtn.eventMode = "static";
    const onHandleTapFunc = () => EventBus.emit(EnumEventBus.setAutoplayPopup, false);
    addInteraction(this.closeBtn, onHandleTapFunc, false);
    // place near the top-right corner of bg
    this.closeBtn.x = bg.width - this.closeBtn.width - 4;
    this.closeBtn.y = -15;
    c.addChild(this.closeBtn);

    // (3) four selectable count buttons
    const counts: CountChoice[] = [5, 10, 15, 20];
    const grid = new PIXI.Container();
    c.addChild(grid);

    // highlight shape that sits behind whichever cell is selected
    this.highlight = new PIXI.Sprite(PIXI.Assets.get("but_autocount_shape"));
    this.highlight.visible = true;
    grid.addChild(this.highlight);

    // build cells
    const COLS = 4;
    const cellGap = 170;
    const cellW = 140; // approximate; sprite width will define actual look
    const cellH = 72;  // approximate
    counts.forEach((value, i) => {
      const cell = new PIXI.Container();

      const btn = new PIXI.Sprite(PIXI.Assets.get("but_autocount"));
      cell.addChild(btn);

      const label = new PIXI.Text(String(value), new PIXI.TextStyle({
        fill: 0xffffff, fontSize: 48, fontWeight: "900",
        dropShadow: {
        alpha: 1,
        angle: 1,
        blur: 2,
        color: "0x111111",
        distance: 2,
      },
      stroke: {
        color: "black",
        width: 5,
        join: "round",
      },
      }));
      label.anchor.set(0.5);
      label.x = btn.width / 2;
      label.y = btn.height / 2;
      cell.addChild(label);

      cell.eventMode = "static";
      addInteraction(cell, () => this.selectCount(value), true)
      // position in a single row like the demo
      const col = i % COLS;
      cell.x = col * (cellW + cellGap);
      cell.y = 160;
      grid.addChild(cell);

      this.countButtons.set(value, cell);
    });

    // center the grid relative to bg
    grid.x = (bg.width - grid.width) / 2;
    grid.y = 100;

    // place highlight behind current selection
    this.snapHighlightTo(this.selected);

     // (4) Start Autoplay button at bottom (sprite + text)
    const startBtn = new PIXI.Container();
    startBtn.eventMode = "static";
    startBtn.cursor = "pointer";

    const startBg = new PIXI.Sprite(PIXI.Assets.get("but_start_autoplay"));
    startBtn.addChild(startBg);

    const startLabel = new PIXI.Text("START AUTOPLAY", new PIXI.TextStyle({
      fill: 0xffffff,
      fontSize: 44,
      fontWeight: "900",
      dropShadow: {
        alpha: 1,
        angle: 1,
        blur: 2,
        color: "0x111111",
        distance: 2,
      },
      stroke: {
        color: "black",
        width: 5,
        join: "round",
      },
    }));
    startLabel.anchor.set(0.5);
    startLabel.x = startBg.width / 2;
    startLabel.y = startBg.height / 2;
    startBtn.addChild(startLabel);

    // feedback & action
    startBtn.on("pointerover", () => startBtn.filters = [brightnessFilter]);
    startBtn.on("pointerout", () => startBtn.filters = []);

    startBtn.on("pointerdown", () => { startBtn.scale.set(0.95); });
    startBtn.on("pointerup", () => {
      startBtn.scale.set(1);
      //this.start(this.selected);
      EventBus.emit(EnumEventBus.setAutoplayGame, true, this.selected);
      this.closeModal();
    });
    startBtn.on("pointerupoutside", () => { startBtn.scale.set(1); });

    startBtn.x = (bg.width - startBg.width) / 2;
    startBtn.y = bg.height - startBg.height -84;
    c.addChild(startBtn);

    return c;
  }

  private selectCount(value: CountChoice) {
    SoundManager.Btnclick_1SndPlay();
    this.selected = value;
    this.snapHighlightTo(value);
  }

  private snapHighlightTo(value: CountChoice) {
    const cell = this.countButtons.get(value);
    if (!cell) return;
    // move the highlight behind that cell, slightly larger look is handled by the art
    this.highlight.visible = true;
    // put highlight just under the cell's children
    this.highlight.zIndex = -1;
    // align top-left to the cell (tweak offsets if your art needs)
    this.highlight.x = cell.x;
    this.highlight.y = cell.y;
    this.countButtons.forEach(b => b.interactive = true);
    cell.interactive = false;
  }

  private openModal() {
    SoundManager.Btnclick_1SndPlay();
    this.modal.position.set(0, 0);
    this.modal.visible = true;
    this.layout();
  }

  private closeModal() {
    SoundManager.Btnclick_1SndPlay();
    this.modal.visible = false;
  }

  private resolveScreen(): CardScreen | null {
    if (this.screen) return this.screen;
    if (this.screenGetter) {
      const resolved = this.screenGetter();
      if (resolved) {
        this.screen = resolved;
        return resolved;
      }
    }
    return null;
  }

  private ensureScreen(): CardScreen {
    const screen = this.resolveScreen();
    if (!screen) {
      throw new Error("CardScreen not ready for autoplay");
    }
    return screen;
  }

  private tryWireScreen() {
    if (this.isScreenWired) return;
    const screen = this.resolveScreen();
    if (!screen) return;

    screen.on?.("roundstart", () => {
      this.inRound = true;
    });

    screen.on?.("roundend", async () => {
      this.inRound = false;
      if (!this.isRunning) return;

      if (this.left > 0) {
        this.left--;
      }
      this.updateCountLabel();

      if (this.left === 0) {
        EventBus.emit(EnumEventBus.setAutoplayGame, false);
        if(LogoAnim.insideBonus){
          LogoAnim.StartBut_disableFun?.();
        }
        return;
      }
      
      await this.maybeSpin();
    });

    this.isScreenWired = true;
  }

  private lockManualUi(lock: boolean) {
    if (lock) {
      LogoAnim.StartBut_disableFun?.();
    } else if (!this.isRunning) {
      LogoAnim.StartBut_enableFun?.();
    }

    const betUi = (window as any)?.betArea as betArea | undefined;
    if (!betUi) return;

    const toggleButton = (container?: PIXI.Container, enabled = false) => {
      if (!container) return;
      container.interactive = enabled;
      (container as any).cursor = enabled ? "pointer" : "not-allowed";
      container.filters = enabled ? [] : [disabledfilter];
    };

    const plusEnabled = !lock && betArea.currentBetIndex < betSizes.length - 1;
    const minusEnabled = !lock && betArea.currentBetIndex > 0;

    toggleButton(betUi.plusContainer, plusEnabled);
    toggleButton(betUi.minusContainer, minusEnabled);

    if (betUi.betTextContainer) {
      betUi.betTextContainer.interactive = !lock;
      (betUi.betTextContainer as any).cursor = !lock ? "pointer" : "not-allowed";
      betUi.betTextContainer.filters = !lock ? [] : [disabledfilter];
    }
  }

  private updateCountLabel() {
    EventBus.emit(EnumEventBus.setAutoPlayCount, this.left);
    if (!this.countLabel) return;
    this.countLabel.text = `${Math.max(this.left, 0)}`;
    this.countLabel.visible = this.isRunning && this.left > 0;
  }


  // ---------------- Game flow wiring ----------------
  private wireScreen() {
    this.tryWireScreen();
  }

  private async maybeSpin() {
    this.tryWireScreen();
    if (!this.isRunning || this.inRound) return;

    if (!this.resolveScreen()||LogoAnim.insideBonus) {
      setTimeout(() => {
        if (this.isRunning) {
          this.maybeSpin();
        }
      }, 16);
      return;
    }

    try {
      await this.spin();
    } catch (err) {
      console.warn("Autoplay spin failed:", err);
      this.stop();
    }
  }

  // ---------------- Public API ----------------
  /** Requirement #5 + #10 */
  public start(count: CountChoice) {
    if (this.isRunning) return;
    SoundManager.Btnclick_1SndPlay();
    this.left = count;
    this.isRunning = true;

    this.tryWireScreen();
    this.lockManualUi(true);

    this.updateCountLabel();
    this.maybeSpin();

    this.emit?.("autoplay:start", { left: this.left });
  }

  /** Requirement #8 */
  public stop() {
    if (!this.isRunning) return;
    this.isRunning = false;
    this.left = 0;

    this.lockManualUi(false);


    this.updateCountLabel();

    this.emit?.("autoplay:stop");
  }

  public isRunningNow(): boolean {
    return this.isRunning;
  }

  /** Call if you programmatically want to show the popup */
  public open() { this.openModal(); }
  /** Keep stop button aligned with autoplay button if you move it externally */
  public syncButtons() { this.layout(); }
  PortraitAdjustFun(){
    if(this.modal) {
        this.modal.x = 190
        this.modal.y = 150;
        this.modal.scale.set(0.65);
      }
  }  
  landScapeAdjustFun(){
    if(this.modal){
        this.modal.x = 190
        this.modal.y = 150;
        this.modal.scale.set(.65);
      }
  }  
}


